import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import ToastMsg from "../../components/ToastMsg/ToastMsg";
import config from "../../config/config";
import { supporterPlan } from "../../images";
import { businessMagicLogin } from "../../components/helpers/constant-words";
import "./SpecialCheckout.scss";

const SpecialCheckout = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState("loading"); // loading | ready | invalid | success
  const [errorMsg, setErrorMsg] = useState("");
  const [linkData, setLinkData] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [billing, setBilling] = useState({
    billingName: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    gstNo: "",
  });

  useEffect(() => {
    const validate = async () => {
      try {
        const res = await axios.get(
          `${config.api_url}/special-payment/validate/${token}`
        );
        if (res.data?.data) {
          setLinkData(res.data.data);
          setPageState("ready");
        } else {
          setErrorMsg("Invalid or expired link.");
          setPageState("invalid");
        }
      } catch (err) {
        const msg =
          err.response?.data?.message || "This link is invalid or has expired.";
        setErrorMsg(msg);
        setPageState("invalid");
      }
    };
    validate();
  }, [token]);

  const handleBillingChange = (e) => {
    setBilling((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const initiatePayment = async (e) => {
    e.preventDefault();

    if (!billing.billingName || !billing.address_1 || !billing.city || !billing.state || !billing.pincode) {
      toast(
        <ToastMsg message="Please fill in all required billing fields" danger />,
        config.error_toast_config
      );
      return;
    }

    setPaymentLoading(true);
    try {
      const orderRes = await axios.post(
        `${config.api_url}/special-payment/${token}/create-order`,
        { billingDetails: billing }
      );

      const { orderId, amount, businessName, email, phone } =
        orderRes.data?.data || {};

      if (!orderId) {
        throw new Error("Failed to create order");
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const options = {
          key: config.razorpay_key_id,
          order_id: orderId,
          amount,
          currency: "INR",
          name: "Archinza",
          description: "Supporter Plan — Special Offer",
          handler: async (response) => {
            try {
              const verifyRes = await axios.post(
                `${config.api_url}/special-payment/${token}/verify-order`,
                {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                }
              );
              if (verifyRes.data?.data?.success) {
                const magicToken = verifyRes.data?.data?.magicToken;
                navigate(`${businessMagicLogin}?token=${magicToken}`);
              } else {
                throw new Error("Verification failed");
              }
            } catch {
              toast(
                <ToastMsg
                  message="Payment verification failed. Please contact support."
                  danger
                />,
                config.error_toast_config
              );
            } finally {
              setPaymentLoading(false);
            }
          },
          prefill: {
            name: (businessName || "").replace(/[^a-zA-Z\s]/g, "").trim() || "Customer",
            email: email || "",
            contact: phone || "",
          },
          theme: { color: "#014fe0" },
          modal: {
            ondismiss: () => setPaymentLoading(false),
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      };

      script.onerror = () => {
        toast(
          <ToastMsg message="Failed to load payment gateway" danger />,
          config.error_toast_config
        );
        setPaymentLoading(false);
      };
    } catch {
      toast(
        <ToastMsg message="Failed to initiate payment. Please try again." danger />,
        config.error_toast_config
      );
      setPaymentLoading(false);
    }
  };

  if (pageState === "loading") {
    return (
      <div className="sc__center">
        <p className="sc__loading">Validating your link...</p>
      </div>
    );
  }

  if (pageState === "invalid") {
    return (
      <div className="sc__center">
        <div className="sc__invalid-card">
          <h2 className="sc__invalid-title">Link Unavailable</h2>
          <p className="sc__invalid-msg">{errorMsg}</p>
        </div>
      </div>
    );
  }

  if (pageState === "success") {
    return (
      <div className="sc__center">
        <div className="sc__success-card">
          <div className="sc__success-icon">✓</div>
          <h2 className="sc__success-title">Payment Successful!</h2>
          <p className="sc__success-msg">
            Welcome to the <strong>Archinza Supporter Plan</strong>. A
            confirmation has been sent to your email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="sc__wrap">
      {/* Desktop only — benefits sidebar to the left of the card */}
      <div className="sc__benefits-sidebar">
        <p className="sc__benefits-title">What's included with Supporter Plan</p>
        <img src={supporterPlan} alt="Supporter Plan Benefits" className="sc__benefits-img" />
      </div>

      <div className="sc__card">
        <div className="sc__brand">Archinza</div>
        <h1 className="sc__title">Complete Your Upgrade</h1>

        <div className="sc__summary">
          <div className="sc__summary-row">
            <span className="sc__summary-label">Business</span>
            <span className="sc__summary-value">{linkData.businessName}</span>
          </div>
          <div className="sc__summary-row">
            <span className="sc__summary-label">Plan</span>
            <span className="sc__summary-value">Supporter Plan</span>
          </div>
          <div className="sc__summary-divider" />
          <div className="sc__summary-row sc__summary-row--total">
            <span className="sc__summary-label">Amount</span>
            <span className="sc__summary-amount">₹{linkData.amount}</span>
          </div>
          <p className="sc__summary-note">Valid for 1 year from payment date</p>
        </div>

        {/* Mobile only — benefits inside card between summary and form */}
        <div className="sc__benefits-mobile">
          <p className="sc__benefits-title">What's included with Supporter Plan</p>
          <img src={supporterPlan} alt="Supporter Plan Benefits" className="sc__benefits-img" />
        </div>

        <form onSubmit={initiatePayment} className="sc__form">
          <h3 className="sc__form-title">Billing Details</h3>

          <div className="sc__form-group">
            <label className="sc__label">Billing Name *</label>
            <input
              className="sc__input"
              name="billingName"
              value={billing.billingName}
              onChange={handleBillingChange}
              required
            />
          </div>

          <div className="sc__form-group">
            <label className="sc__label">Address Line 1 *</label>
            <input
              className="sc__input"
              name="address_1"
              value={billing.address_1}
              onChange={handleBillingChange}
              required
            />
          </div>

          <div className="sc__form-group">
            <label className="sc__label">Address Line 2</label>
            <input
              className="sc__input"
              name="address_2"
              value={billing.address_2}
              onChange={handleBillingChange}
            />
          </div>

          <div className="sc__form-row">
            <div className="sc__form-group">
              <label className="sc__label">City *</label>
              <input
                className="sc__input"
                name="city"
                value={billing.city}
                onChange={handleBillingChange}
                required
              />
            </div>
            <div className="sc__form-group">
              <label className="sc__label">State *</label>
              <input
                className="sc__input"
                name="state"
                value={billing.state}
                onChange={handleBillingChange}
                required
              />
            </div>
          </div>

          <div className="sc__form-row">
            <div className="sc__form-group">
              <label className="sc__label">Pincode *</label>
              <input
                className="sc__input"
                name="pincode"
                value={billing.pincode}
                onChange={handleBillingChange}
                required
              />
            </div>
            <div className="sc__form-group">
              <label className="sc__label">GST Number (optional)</label>
              <input
                className="sc__input"
                name="gstNo"
                value={billing.gstNo}
                onChange={handleBillingChange}
                placeholder="22AAAAA0000A1Z5"
              />
            </div>
          </div>

          <button
            type="submit"
            className="sc__pay-btn"
            disabled={paymentLoading}
          >
            {paymentLoading ? "Processing..." : `Pay ₹${linkData.amount}`}
          </button>

          <p className="sc__secure-note">
            Secured by Razorpay · 256-bit SSL encryption
          </p>
        </form>
      </div>
    </div>
  );
};

export default SpecialCheckout;
